/**
 * @module app.track.TracksController
 */
import {MAX_POINTS_FOR_A_TRACK, TRACK_COLOR_VALUES, TRACK_DEFAULT_COLOR, TRACK_WIDTH, Z_INDEX} from '../constants.js';
import {
  calculateViaPoints,
  shiftPositionAlongLine,
  roundCoordinates,
  colorWithOpacity,
  lonLatToMapProjection, olAssignClosest
} from '../utils.js';
import appTrackService from '../track/service.js';
import appTrackFilterService from '../track/filterService.js';
import appTrackProfileService from '../track/ProfileService.js';
import appTrackPrintService from './trackPrintService.js';
import ngeoStatemanagerLocationModule from 'ngeo/statemanager/Location';
import {distance as olDistance, squaredDistance as olSquaredDistance} from 'ol/coordinate.js';
import olFeature from 'ol/Feature.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olFormatPolyline from 'ol/format/Polyline.js';
import olGeomLineString from 'ol/geom/LineString.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleStyle from 'ol/style/Style.js';
import appTrackProfile from '../track/Profile.js';
import olGeomPoint from 'ol/geom/Point';
import olStyleFill from 'ol/style/Fill.js';
import olStyleCircle from "ol/style/Circle.js";
import olStyleText from "ol/style/Text.js";
import {makeIcon, SIZE as IMG_SIZE} from "./poiicon";


/**
 * @type {number}
 */
const DEFAULT_OPACITY = 100;


/**
 * @param {Object} meta
 * @param {Array<Array<number>>} profile
 * @return {boolean}
 */
function hasFactsError(meta, profile) {
  return meta['length'] == 0 || profile.some(coord => coord[2] == 0);
}


/**
 * @param {angular.Scope} $scope Angular scope.
 * @param {angular.$http} $http Angular Http service.
 * @param {angular.$timeout} $timeout Angular timeout service.
 * @param {string} apiUrl API url prefix.
 * @param {string} s3Url S3 url prefix.
 * @param {angular.$q} $q Angular Q service.
 * @param {ngeo.statemanager.Location} ngeoLocation Location service from NGEO.
 * @param {angularGettext.Catalog} gettextCatalog
 * @param {app.AuthenticationService} appAuthenticationService
 * @param {app.MapService} appMapService
 * @param {app.track.service} appTrackService
 * @param {app.track.filterService} appTrackFilterService
 * @param {!app.CesiumLoader} appCesiumLoader
 * @param {app.track.ProfileService} appTrackProfileService
 * @param {ui.router.$state} $state
 * @param {angular.$filter} $filter Filter service.
 * @param {app.waiting} appWaiting
 * @constructor
 * @ngInject
 * @struct
 */
class TracksController {
  constructor(
    $scope,
    $http,
    $timeout,
    apiUrl,
    s3Url,
    $q,
    ngeoLocation,
    gettextCatalog,
    appAuthenticationService,
    appMapService,
    appTrackService,
    appTrackFilterService,
    appCesiumLoader,
    appTrackProfileService,
    $state,
    $filter,
    appWaiting
  ) {
    // TODO refactor modals and remove this
    $scope.$root['modalsTracksCtrl'] = this;

    $scope.$root.$on('SSE_REFRESH', () => {
      console.log('reloading tracks list');
      this.getUserTracksList_(() => {
        console.log('tracks list reloaded');
      });
    });

    /**
     * @private
     */
    this.timeout_ = $timeout;

    /**
     * @type {boolean}
     * @private
     */
    this.validProfile_ = true;

    /**
     * @type {string}
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @type {string}
     * @private
     */
    this.s3Url_ = s3Url;

    /**
     * @private
     */
    this.authentication_ = appAuthenticationService;

    /**
     * @export
     */
    this.appTrackService = appTrackService;

    /**
     * @export
     */
    this.appTrackFilterService = appTrackFilterService;

    /**
     * @private
     */
    this.appTrackProfileService_ = appTrackProfileService;

    /**
     * @private
     */
    this.cesium_ = appCesiumLoader;

    /**
     * @export
     */
    this.routingState = $state;

    /**
     * @export
     * @type {boolean}
     */
    this.factsError = false;

    /**
     * @export
     * @type {number}
     */
    this.factsLoading = 0;

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @private
     */
    this.q_ = $q;

    /**
     * @private
     */
    this.http_ = $http;

    /**
     * @private
     */
    this.dateFilter_ = $filter('date');

    /**
     * @private
     */
    this.appWaiting_ = appWaiting;

    /**
     * @type {boolean}
     * @private
     */
    this.forceTrackNameChecks_ = false;

    /**
     * @type {?angular.$q.Deferred}
     * @private
     */
    this.trackInfoCancelPromise_ = null;

    /**
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * Don't create a private property to allow angular-gettext extraction.
     */
    this.gettextCatalog = gettextCatalog;

    /**
     * @type {ol.Map}
     * @export
     */
    this.map = appMapService.map;

    /**
     * @export
     * @type {function(appx.SecondLineChangeEvent)}
     */
    this.onSecondLineChanged;

    /**
     * @type {function()}
     * @export
     */
    this.onShowTracksMenu;

    /**
     * @export
     * @type {boolean}
     */
    this.allowTrackImport;

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.geojsonFormat_ = new olFormatGeoJSON();

    /**
     * @type {ol.Feature}
     * @export
     */
    this.modifyFeature;

    /**
     * @type {Object}
     * @private
     */
    this.trackBackup_;

    /**
     * @type {Array}
     * @export
     */
    this.tracks_list = null;

    /**
     * @type {number|undefined}
     * @export
     */
    this.selectedTrackId = undefined;

    /**
     * @type {angular.$q.Promise|undefined}
     * @private
     */
    this.savePromise_;

    /**
     * @type {angular.$q.Promise|undefined}
     * @private
     */
    this.trackInfoPromise_;

    /**
     * @type {number|undefined}
     * @export
     */
    this.track_id_param;

    /**
     * @type {appx.TrackShowResponse|undefined}
     * @private
     */
    this.trackFromCoordinates_;

    /**
     * @type {!Array.<string>}
     * @export
     */
    this.errorMessages = [];

    /**
     * @export
     */
    this.hasFormData = ('FormData' in window);

    /**
     * @type {ol.layer.Vector}
     * @export
     */
    this.selectedTrackLayer;

    /**
     * @type {ol.layer.Vector}
     * @export
     */
    this.selectedTrackPoisLayer;

    /**
     * @private
     * @type {ol.Feature}
     */
    this.activePOI_;

    /**
     * @export
     */
    this.trackPOIStyleFunction = this.initTrackPOIStyleFunction_();

    /**
     * @type {!Object<string, olStyleStyle[]>}
     * @private
     */
    this.trackPointsStyleCache_ = {};

    $scope.$watch(() => appTrackFilterService.selectedFilter, (filter) => {
      if (this.tracks_list) {
        // Update filter id in track list
        this.tracks_list.forEach(entry => {
          if (entry.id === this.selectedTrackId) {
            entry.filter_id = filter.id;
          }
        });
      }
    });

    this.requestInfoForGeom = (geom) => {
      const params = {
        'featureId': -1
      };
      const feature = new olFeature(geom);
      const simplifiedFeature = this.getEventuallyResampledLineString_(feature);
      params['geom'] = this.geojsonFormat_.writeFeature(simplifiedFeature);
      const makeRequest = () => {
        this.increaseFactsLoading();
        const data = new URLSearchParams(params).toString();
        this.trackInfoRequest = this.http_.post(this.apiUrl_ + '4/track_info', data, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }).then(res => {
          if (!res.data) {
            return null;
          }
          return res.data.profile.map(p => [p.easting, p.northing, p.alts[res.data.elevationmodels]]);
        }).catch((response) => {
          if (response.status !== -1) {
            this.validProfile_ = false;
            this.validateTrackInfo_();
          }
        }).finally(() => {
          this.trackInfoRequest = null;
          this.decreaseFactsLoading();
        });
        return this.trackInfoRequest;
      };

      return this.trackInfoRequest ? this.trackInfoRequest.then(makeRequest) : makeRequest();
    };
  }

  $onInit() {
    this.selectedTrackLayer = new olLayerVector({
      'name': 'selected track',
      type: 'selected_tracks',
      source: new olSourceVector(),
      zIndex: Z_INDEX.UP
    });
    this.map.addLayer(this.selectedTrackLayer);

    this.selectedTrackPoisLayer = new olLayerVector({
      'name': 'selected track pois',
      type: 'selected_tracks',
      source: new olSourceVector(),
      zIndex: Z_INDEX.UP
    });
    this.map.addLayer(this.selectedTrackPoisLayer);
    if (this.ngeoLocation_.hasParam('trackId') || this.ngeoLocation_.hasParam('recordedTrackId')) {
      const trackParam = this.ngeoLocation_.hasParam('trackId') ? 'trackId' : 'recordedTrackId';
      const id = parseInt(this.ngeoLocation_.getParam(trackParam), 10);
      if (isFinite(id) && id > 0) {
        this.track_id_param = id;
      } else {
        setTimeout(() => {
          // translation are not ready in production mode
          this.onImportError_();
        }, 1000);
      }
    } else if (this.ngeoLocation_.hasParam('coordinates')) {
      try {
        const text = this.ngeoLocation_.getParam('coordinates');
        let coordinates = [];
        const format = new olFormatPolyline();
        if (text.startsWith('poly_')) {
          const linestring = /** @type {ol.geom.LineString} */ (format.readGeometry(text.substring(5), {
            featureProjection: 'EPSG:21781'
          }));
          coordinates = linestring.getCoordinates();
        } else {
          coordinates = /** @type {Array<ol.Coordinate>} */ (JSON.parse(text));
        }
        if (coordinates[0][0] > 2000000) {
          // LV95 to LV03
          coordinates = coordinates.map(coo => [coo[0] - 2000000, coo[1] - 1000000]);
        }
        this.trackFromCoordinates_ = this.createEmptyTrack_("-1");
        this.trackFromCoordinates_.geometry.coordinates = coordinates;
      } catch (e) {
        setTimeout(() => {
          // translation are not ready in production mode
          this.onImportError_(e);
        }, 1000);
      }
    }

    this.scope_.$on('featureSelected', (event, feature) => {
      const properties = feature['properties'];
      const type = properties['type'];
      if (type === 'track') {
        this.onShowTracksMenu();
        this.gotoTrackFacts();
      } else if (type === 'track_poi') {
        // The feature we receive is not an ol.Feature
        // so we need to retrieve it from its id.
        const source = this.selectedTrackPoisLayer.getSource();
        const featureId = feature['id'];
        console.assert(featureId !== undefined);
        const activeFeature = source.getFeatureById(featureId);

        this.onShowTracksMenu();
        this.routingState.go('track-poi', {
          'activePOI': activeFeature
        });
      }
    });

    this.scope_.$on('logout', () => {
      this.clearSelectedTrack();
      this.tracks_list = null;
    });

    this.scope_.$on('loginChange', () => {
      this.authentication_.tokenLoginPromise.then(() => {
        this.loginChange_();
      });
    });
    // Sometime, the loginChange event is triggered before the listener is added. So we need to call it manually.
    setTimeout(() => {
      this.authentication_.tokenLoginPromise.then(() => {
        this.loginChange_();
      });
    });

    this.scope_.$watch(
      () => this.getSecondLine_(),
      (secondLine) => {
        this.onSecondLineChanged({secondLine});
      }
    );

    $('#gps_import_modal').on('hidden.bs.modal', function() {
      const file = $('#gps_import_form input[name="trackfile"]'); // eslint-disable-line no-unused-vars
      file.val('').clone(true); // hack to reset file see
      // http://stackoverflow.com/questions/20549241/how-to-reset-input-type-file
      $('#gps_import_form')[0].reset();
    });
  }

  onImportError_(err) {
    const id = 'import-error-alert';
    this.scope_.$root.$broadcast('trackImportError');
    this.routingState.go('user');
    if (!document.getElementById(id)) {
      let boxContent = {
        title: this.gettextCatalog.getString('Track import error'),
        message: this.gettextCatalog.getString("This tour can't be displayed or imported")
      };
      if (err && err.status === 403) {
        boxContent = {
          title: this.gettextCatalog.getString('Track import 403 error title'),
          message: this.gettextCatalog.getString("Track import 403 error content")
        };
      }
      const box = bootbox.alert(boxContent);
      box[0].id = id;
    }
  }

  /**
   * @return {function(this:ol.Feature, number): Array.<ol.style.Style>}
   * @private
   */
  initTrackPOIStyleFunction_() {
    const cache = {};
    const trackService = this.appTrackService;
    const showText = () => !this.modifyFeature;

    /**
     * @this {ol.Feature}
     * @return {Array.<ol.style.Style>}
     */
    const styleFunction = function() {
      // Use an empty marker to store
      // 'this' is the feature
      const featureSort = /** @type {number} */ (this.get('sort'));
      const sort = showText() ? (featureSort || '') : '';
      const {color} = trackService.selectedTrack.properties;
      const cacheKey = `${color}-${sort}`;
      let style = cache[cacheKey];
      if (!style) {
        // we could use a text style for rendering the sort number but it would
        // not work with ol3-cesium and the font would depend on the user
        style = [new olStyleStyle({
          image: new olStyleIcon({
            imgSize: IMG_SIZE,
            src: makeIcon(color, sort),
            anchor: [0.5, 1]
          })
        })];
        cache[cacheKey] = style;
      }
      return style;
    };

    return styleFunction;
  }

  /**
   * @export
   */
  updateTrackProperties() {
    const {color_rgb, opacity} = this.appTrackService.selectedTrack.properties;
    this.selectedTrackLayer.getSource().getFeatures().forEach((feature) => {
      const type = feature.getGeometry().getType();
      console.assert(type === 'LineString' || type === 'MultiLineString' || type === 'Point');
      if (type === 'LineString' || type === 'MultiLineString') {
        feature.set('color_rgb', color_rgb);
        feature.set('opacity', opacity);
      }
    });
    this.selectedTrackPoisLayer.getSource().changed();
  }

  /**
   * @export
   */
  deleteTrack() {
    bootbox.addLocale('delete_track', {
      OK: 'Ok',
      CANCEL: this.gettextCatalog.getString('Cancel deletion'),
      CONFIRM: this.gettextCatalog.getString('Delete')
    });
    bootbox.setLocale('delete_track');
    bootbox.confirm({
      title: this.gettextCatalog.getString('Delete Track'),
      message: this.gettextCatalog.getString('Are you sure you want to delete the track?'),
      callback: (result) => {
        if (result) {
          const trackId = this.selectedTrackId;
          const api = this.appTrackService.selectedTrack.recorded ? '5/recordedtracks/' : '4/tracks/';
          this.http_.delete(`${this.apiUrl_}${api}${trackId}`).then(() => {
            // remove track from list
            let found = false;
            let i = 0;
            for (const ii = this.tracks_list.length; i < ii; i++) {
              if (trackId === this.tracks_list[i].id) {
                found = true;
                break;
              }
            }
            console.assert(found);
            this.tracks_list.splice(i, 1);

            this.clearSelectedTrack();
            this.routingState.go('track-list');
          }).catch((e) => {
            console.error(e);
            bootbox.alert({
              title: this.gettextCatalog.getString('An error occurred'),
              message: this.gettextCatalog.getString('Error when deleting tour')
            });
          });
        }
      }
    });
  }

  /**
   * @export
   */
  logout() {
    this.authentication_.logout();
  }

  /**
   * @export
   * @return {boolean}
   */
  loggedIn() {
    return this.authentication_.loggedIn;
  }

  subscriptionOk() {
    return this.authentication_.subscriptionOk;
  }

  /**
   * @private
   * @return {?string}
   */
  getSecondLine_() {
    const trackScreen = this.routingState.current.name;
    switch (trackScreen) {
      case 'track-facts':
        return this.appTrackService.selectedTrack ? this.appTrackService.selectedTrack.properties.name : '';
      case 'track-config':
        return this.gettextCatalog.getString('Configuration for this tour');
      case 'track-gpx':
        return this.gettextCatalog.getString('Download track for GPS');
      case 'track-list':
        return this.gettextCatalog.getString('My Tours');
      case 'track-edit':
        return this.modifyFeature ?
          this.gettextCatalog.getString('Modify route') :
          this.gettextCatalog.getString('Draw route');
      case 'track-print':
        return this.gettextCatalog.getString('Print route');
      case 'track-poi':
        const sort = this.activePOI_ ? this.activePOI_.get('sort') : undefined;
        return sort ?
          this.gettextCatalog.getString('Photo / Text') + ' ' + sort :
          this.gettextCatalog.getString('Insert Photo / Text');
      case 'track-filter':
        return this.gettextCatalog.getString('Edit filter');
      case 'track-share':
        return this.gettextCatalog.getString('Share');
      case 'user':
        return this.gettextCatalog.getString('SwitzerlandMobility Plus');
      case 'general-config':
        return this.gettextCatalog.getString('General Configuration');
      default:
        return '';
    }
  }

  /**
   * @param {boolean} create
   * @param {!appx.UserSettings} userSettings
   * @export
   */
  initDrawing(create, userSettings) {
    if (!create) {
      // edit the selected track
      this.selectedTrackLayer.setVisible(false);
      // Backup meta and profile to be able to restore on cancel editing
      const properties = this.appTrackService.selectedTrack.properties;
      this.trackBackup_ = {
        meta_backup: Object.assign({}, properties.meta),
        name_backup: properties.name,
        profile_backup: properties.profile.slice(),
        color_backup: properties.color,
        color_rgb_backup: properties.color_rgb,
        opacity_backup: properties.opacity,
        velospeed_backup: properties.velospeed,
      };
      console.assert(this.appTrackService.selectedTrack);
      this.modifyFeature = this.geojsonFormat_.readFeature(this.appTrackService.selectedTrack);
      this.selectedTrackPoisLayer.getSource().changed();
      // inject the altitude into the track
      const profile = new olGeomLineString(/** @type {Array.<Array.<number>>} */ (this.modifyFeature.get('profile')));
      const xy = /** @type {ol.geom.LineString} */ (this.modifyFeature.getGeometry()).getCoordinates();
      this.modifyFeature.setGeometry(this.addAltitudeFromProfile_(xy, profile));
    } else {
      //draw a new track
      this.clearSelectedTrack();
      this.appTrackService.selectedTrack = this.createEmptyTrack_(this.authentication_.userId, userSettings);
      this.appTrackService.selectedTrack.properties.userdate = this.dateFilter_(new Date(), 'dd.MM.yyyy');
    }
  }

  /**
   * @param {string|undefined} userId
   * @param {appx.UserSettings=} userSettings
   * @return {appx.TrackShowResponse}
   */
  createEmptyTrack_(userId, userSettings = {}) {
    const color = userSettings.color || TRACK_DEFAULT_COLOR;

    /**
     * @type {appx.TrackMeta}
     */
    const trackMeta = {
      length: 0,
      totalup: 0,
      totaldown: 0,
      elemin: 0,
      elemax: 0,
      walking: 0,
      elevationmodels: 'ElevationModel Snapped',
      biking: 0,
      heightdiff: 0,
      winter_hiking: 0,
      snowshoe_trekking: 0
    };

    /**
     * @type {appx.TrackProperties}
     */
    const trackProperties = {
      meta: trackMeta,
      name: '',
      owner: userId || '-1',
      profile: [],
      via_points: [],
      color: color,
      color_rgb: TRACK_COLOR_VALUES[color].color,
      filterid: userSettings.filterid,
      gpspoints: userSettings.gps_points,
      opacity: userSettings.opacity || DEFAULT_OPACITY,
      timetype: !userSettings.timetype ||
      userSettings.timetype === 'mountain_biking' ||
      userSettings.timetype === 'skating' ||
      userSettings.timetype === 'canoeing' ||
      userSettings.timetype === 'cross_country_skiing' ||
      userSettings.timetype === 'sledging' ? 'no' : userSettings.timetype,
      velospeed: userSettings.bicycle_speed || 12,
      public: userSettings.public,
    };

    /**
     * @type {appx.TrackShowResponse}
     */
    const track = {
      geometry: {
        coordinates: [],
        type: 'LineString'
      },
      type: 'Feature',
      properties: trackProperties
    };

    return track;
  }

  /**
   * @param {Array<ol.Coordinate>} xy
   * @param {ol.geom.LineString} profile
   * @return {ol.geom.LineString}
   */
  addAltitudeFromProfile_(xy, profile) {
    const kdTree = new window['kdTree'](profile.getCoordinates(), olSquaredDistance, [0, 1]);

    return new olGeomLineString(xy.map(function(point) {
      let altitude = 0;
      const pointsNearby = kdTree['nearest'](point, 2);
      if (pointsNearby.length === 1) {
        altitude = pointsNearby[0][0][2];
      } else if (pointsNearby.length === 2) {
        const [[point0], [point1]] = pointsNearby;
        const closest = [];
        olAssignClosest([...point0, ...point1], 0, 4, 4, point[0], point[1], closest);
        altitude = closest[2];
      }
      return [point[0], point[1], altitude];
    }), 'XYZ');
  }

  /**
   * @param {string} value
   * @export
   */
  setTimetype(value) {
    const selectedTrack = this.appTrackService.selectedTrack;
    console.assert(selectedTrack);
    if (selectedTrack.properties.timetype != value) {
      selectedTrack.properties.timetype = value;
      // save the configuration only if the user update it's own track.
      if (selectedTrack.id && this.appTrackService.ownSelectedTrack()) {
        this.appTrackService.saveTrackConfig(selectedTrack);
      }
    }
  }

  /**
   * @param {number} id the Id of the feature
   * @param {'normal'|'recordedtrack'} type
   * @param {{clear: boolean, skip_zoom: boolean}=} options
   * @return {!angular.$q.Promise}
   * @export
   */
  selectTrack(id, type, options = {}) {
    if (this.ngeoLocation_.hasParam('coordinates')) {
      this.ngeoLocation_.deleteParam('coordinates');
      this.trackFromCoordinates_ = undefined;
    }
    if (id === this.selectedTrackId) {
      if (options.clear) {
        // The track was deselected from the list (checkbox unchecked)
        this.clearSelectedTrack();
      }
      return this.q_.resolve();
    }
    const isRecordedTrack = type === 'recordedtrack';
    const param = isRecordedTrack ? 'recordedTrackId' : 'trackId';
    this.ngeoLocation_.deleteParam('trackId');
    this.ngeoLocation_.deleteParam('recordedTrackId');
    this.ngeoLocation_.updateParams({[param]: id});

    const trackPromise = isRecordedTrack ? this.getRecordedTrackShow_(id) : this.getUserTrackShow_(id);
    this.appWaiting_.increment();
    return trackPromise.then(response => this.handleUserTrackShow_(response, options.skip_zoom))
      .finally(() => {
        this.appWaiting_.decrement();
      });
  }

  /**
   * @param {appx.TrackShowResponse} geojson
   * @param {boolean=} opt_skip_zoom
   * @private
   */
  handleUserTrackShow_(geojson, opt_skip_zoom) {
    const properties = geojson.properties;
    const profile = properties.profile;
    const coordinates = geojson.geometry.coordinates;
    geojson.type = geojson.type ? geojson.type : 'Feature';
    const lineFeature = this.geojsonFormat_.readFeature(geojson);
    // Show track
    const trackSource = this.selectedTrackLayer.getSource();
    trackSource.clear();
    const trackPoisSource = this.selectedTrackPoisLayer.getSource();
    trackPoisSource.clear();
    this.showTrackPoints(trackSource, lineFeature, properties, coordinates);

    /**
     * @this {ol.Feature}
     * @return {Array.<ol.style.Style>}
     */
    const trackStyle = function() {
      let opacity = /** @type {Number} */ (this.get('opacity'));
      const color = /** @type {string} */ (this.get('color_rgb'));
      if (opacity === undefined || opacity === null) {
        // on track display, we have not yet recovered the user default track
        // values, so the opacity may not be defined yet (aka, be null)
        opacity = DEFAULT_OPACITY;
      }

      return [
        new olStyleStyle({
          stroke: new olStyleStroke({
            // see hack to skip this style in app.FeatureConverter
            color: [255, 255, 255, 0.01],
            width: 15
          })
        }),
        new olStyleStyle({
          stroke: new olStyleStroke({
            color: colorWithOpacity(color, opacity / 100),
            width: TRACK_WIDTH
          })
        })
      ];
    };

    const elevationFix = this.ngeoLocation_.hasParam('eleFix') ?
      parseInt(this.ngeoLocation_.getParam('eleFix'), 10) : 10;

    // We need to actually display two different track geometries in 2D and 3D.
    // geometry2D: the real track
    // geometry4D: the profile "track": XY, elevation, distance to the first point
    // We use the olcs.3d_geometry feature property to specify a different
    // geometry for Cesium.
    const geometry4D = new olGeomLineString(profile.map(function(p) {
      p = p.slice(); // copy
      p[2] = p[2] + elevationFix;
      return p;
    }), 'XYZM');

    lineFeature.set('olcs.3d_geometry', geometry4D);
    lineFeature.setStyle(trackStyle.bind(lineFeature));
    trackSource.addFeature(lineFeature);
    const size = this.map.getSize();
    console.assert(size);
    let fitOptions;
    if (size) {
      fitOptions = /** @type{olx.view.FitOptions}*/({size});
    }
    if (!opt_skip_zoom) {
      this.map.getView().fit(trackSource.getExtent(), fitOptions);
    }
    this.selectedTrackId = geojson.id;
    this.appTrackService.selectedTrack = geojson;

    const trackProperties = this.appTrackService.selectedTrack.properties;
    const userdate = trackProperties.userdate;
    if (userdate && (typeof userdate === 'number' || userdate.includes('-'))) {
      trackProperties.userdate = this.dateFilter_(new Date(trackProperties.userdate), 'dd.MM.yyyy');
    }
    this.appTrackService.selectedTrackPois = null;
    if (geojson.id && !this.appTrackService.selectedTrack.recorded) {
      this.refreshPOIs(); // retrieve POIs - profile will be refreshed with them when done
    }
    // reinitialize trackfilter (init filter list and select filter id from track)
    this.appTrackFilterService.init();
    if (this.ngeoLocation_.hasParam('showLogin') && !this.authentication_.loggedIn) {
      this.routingState.go('user');
    } else {
      this.gotoTrackFacts();
    }
  }

  /**
   * @export
   * @return {jQuery.Promise}
   */
  refreshPOIs() {
    const trackPoisSource = this.selectedTrackPoisLayer.getSource();
    const id = this.selectedTrackId;
    console.assert(id !== undefined);
    const deferred = jQuery.Deferred();
    const recorded = this.appTrackService.selectedTrack.recorded;
    const promise = this.getUserTrackPOIs_(id, recorded);

    promise.then((response) => {
      // First scale the dist according to the track length
      const data = response.data;
      const profile = this.appTrackService.selectedTrack.properties.profile;
      const profileLength = profile[profile.length - 1][3];
      data['features'].forEach(function(f) {
        const properties = f['properties'];
        properties['dist'] = properties['dist'] * profileLength;
        properties['altitudeMode'] = 'clampToGround';
      });
      // Then use the data
      this.appTrackService.selectedTrackPois = data;
      this.setElevationToPois_();
      const features = this.geojsonFormat_.readFeatures(data);
      features.forEach(function(feature) {
        feature.setStyle(this.trackPOIStyleFunction.bind(feature));
      }, this);
      // Clear track source of POIs
      trackPoisSource.clear();
      trackPoisSource.addFeatures(features);
      deferred.resolve(features);
    }, () => {
      deferred.reject();
    });

    return deferred;
  }

  /**
   * @param {appx.TrackImportResponse} data
   * @private
   */
  onTrackImported_(data) {
    const isRecorded = this.appTrackService.selectedTrack.recorded;
    const type = isRecorded ? 'recordedtrack' : 'normal';
    const param = isRecorded ? 'recordedTrackId' : 'trackId';
    this.ngeoLocation_.updateParams({[param]: data.id});
    if (data.newly_imported) {
      this.selectedTrackId = data.id;
      this.appTrackService.selectedTrack.id = data.id;
      this.addToTracksList({id: data.id, name: data.name, type: type});
      const color = TRACK_DEFAULT_COLOR;
      const {properties} = this.appTrackService.selectedTrack;
      properties.color = color;
      properties.color_rgb = TRACK_COLOR_VALUES[color].color;
      properties.public = data.public;
      this.updateTrackProperties();
      bootbox.alert({
        title: this.gettextCatalog.getString('Tour imported'),
        message: this.gettextCatalog.getString('The track was imported')
      });
    } else {
      this.selectTrack(data.id, type);
      bootbox.alert({
        title: this.gettextCatalog.getString('Tour not imported'),
        message: this.gettextCatalog.getString('The track was not imported: already imported before')
      });
    }
    if (!isRecorded) {
      this.refreshPOIs();
    } else {
      this.selectedTrackPoisLayer.getSource().clear();
    }
  }

  /**
   * @private
   */
  importTrackFromCoordinates_() {
    const question = this.gettextCatalog.getString('Do you want to import this tour into your tour list?');
    const mandatoryname = this.gettextCatalog.getString('coordinates import name is mandatory');
    const box = bootbox.prompt({
      maxlength: 40,
      required: true,
      buttons: {
        confirm: {
          label: this.gettextCatalog.getString('Yes')
        },
        cancel: {
          label: this.gettextCatalog.getString('No')
        }
      },
      title: this.gettextCatalog.getString('Import track'),
      placeholder: this.gettextCatalog.getString('name field placeholder text'),
      message: `${question} <br> <span class="geometryimportboxwarning">${mandatoryname}</span> <br>`,
      callback: trackName => {
        if (trackName) {
          const formData = new FormData();
          const geojson = JSON.stringify(this.trackFromCoordinates_);
          const blob = new Blob([geojson], {
            type: 'text/plain'
          });
          formData.append('trackname', trackName);
          formData.append('trackfile', blob, 'track_from_coordinates.geojson');
          this.importFormData_(formData).then(trackId => {
            if (trackId) {
              this.ngeoLocation_.deleteParam('coordinates');
            }
          }).then(() => {
            bootbox.alert({
              title: this.gettextCatalog.getString('coordinates import confirmation title'),
              message: this.gettextCatalog.getString('coordinates import confirmation text'),
            });
          });

        }
      }
    });
    box.on('shown.bs.modal', () => {
      $("input.bootbox-input-text").blur();
      $("button.bootbox-accept").click(()=>{
        if (!$("input.bootbox-input-text")[0].value) {
          $("div.bootbox-prompt-message > span").show();
        }
      });
      // bug with bootbox maxlength, setting the option manually
      box.find('input').attr({'maxlength': 40}).prop({'required': true});
    });
  }

  /**
   * @param {number} trackId
   * @param {Object} userTracks
   * @private
   */
  importTrack_(trackId, userTracks) {
    const ownTrack = userTracks.some(function(track) {
      return track.id === trackId;
    });
    if (!ownTrack && this.allowTrackImport) {
      if (document.body.classList.contains('in-maintenance')) {
        bootbox.alert({
          message: this.gettextCatalog.getString('Tour not imported')
        });
        return;
      }
      const type = this.ngeoLocation_.hasParam('recordedTrackId') ? 'recordedtrack' : 'normal';
      this.selectTrack(trackId, type).then(() => this.showImportPopup(trackId));
    }
  }

  showImportPopup(trackId) {
    bootbox.confirm({
      buttons: {
        confirm: {
          label: this.gettextCatalog.getString('Yes')
        },
        cancel: {
          label: this.gettextCatalog.getString('No')
        }
      },
      title: this.gettextCatalog.getString('Import track'),
      message: this.gettextCatalog.getString('Do you want to import this tour into your tour list?'),
      callback: importConfirmed => {
        if (importConfirmed) {
          this.getUserTrackCopy_(trackId, data => this.onTrackImported_(data));
        }
      }
    });
  }

  /**
   * @export
   */
  clearSelectedTrack() {
    this.selectedTrackLayer.getSource().clear();
    this.selectedTrackPoisLayer.getSource().clear();
    this.selectedTrackId = undefined;
    this.appTrackService.selectedTrack = undefined;
    this.ngeoLocation_.deleteParam('trackId');
    this.ngeoLocation_.deleteParam('recordedTrackId');
    this.track_id_param = undefined;
    this.trackFromCoordinates_ = undefined;
  }

  /**
   * @param {function(?)} func The function to apply on success.
   * @private
   */
  getUserTracksList_(func) {
    this.http_.get(this.apiUrl_ + '5/tracks').then((response) => {
      const data = response.data;
      console.assert(data instanceof Array);
      this.tracks_list = data;
      this.sortTrackListByName();
      func(data);
    }).catch((error) => {
      // silently ignore errors
    });
  }

  /**
   * @param {number} id the user track id.
   * @param {function(?)} func The function to apply on success.
   * @private
   */
  getUserTrackCopy_(id, func) {
    this.appWaiting_.increment();
    const isRecorded = this.appTrackService.selectedTrack.recorded;
    const api = isRecorded ? '5/recordedtracks/' : '4/tracks/';
    this.http_.put(`${this.apiUrl_}${api}${id}/copy`, {}).then((response) => {
      console.assert(this.authentication_.userId !== undefined);
      this.appTrackService.selectedTrack.properties.owner = this.authentication_.userId;
      func(response.data);
    }).catch((error) => {
      // silently ignore errors
    }).finally(() => {
      this.appWaiting_.decrement();
    });
  }

  /**
   * @export
   * get new facts from server (after updating the track config), to get
   * recalculated velo/wander time
   */
  updateFacts() {
    this.getTrackMeta().then(() => this.gotoTrackFacts());
  }

  getTrackMeta() {
    const geom = this.appTrackService.selectedTrack.geometry;
    if (geom && geom.coordinates) {
      if (geom.coordinates.length > MAX_POINTS_FOR_A_TRACK) {
        return Promise.resolve();
      }
    }
    return this.http_.get(this.apiUrl_ + '4/tracks/' + this.appTrackService.selectedTrack.id + '/getmeta').catch(() => {
      bootbox.alert({
        title: this.gettextCatalog.getString('An error occurred'),
        message: this.gettextCatalog.getString('An error occurred when trying to get track meta')
      });
    });
  }

  /**
   * @export
   */
  gotoTrackFacts() {
    this.routingState.go('track-facts');
  }

  /**
   * @param {appx.TrackProfile} profile
   * @return {import('ol/format/GeoJSON.js').GeoJSONLineString}
   */
  geojsonFromProfile(profile) {
    const coordinates = profile.map(p => p.slice(0, 3));
    return {
      coordinates: coordinates,
      type: 'LineString',
    };
  }

  /**
   * @param {number} id the user track id.
   * @param {boolean} skipProfileUpdate
   * @return {!angular.$q.Promise}
   * @private
   */
  getUserTrackShow_(id) {
    return this.http_.get(`${this.apiUrl_}4/tracks/${id}`).then((response) => {
      // First fix the bad geojson we have received so that it matches
      // appx.TrackShowResponse.
      let geojson = response.data;

      const fixType = function(key) {
        const value = geojson['properties'][key] || '[]';
        return /** @type {Array.<ol.Coordinate>} */ (JSON.parse(value));
      };
      geojson['properties']['profile'] = fixType('profile');
      geojson['properties']['via_points'] = fixType('via_points');

      // From now on the geojson is typed correctly
      geojson = /** @type {appx.TrackShowResponse} */ (geojson);

      geojson.geometry = this.geojsonFromProfile(geojson['properties']['profile']);

      // Handle null geometry
      if (geojson.geometry === null) {
        geojson.geometry = {
          coordinates: [],
          type: 'LineString'
        };
      }

      // Handle error with profile/meta (should happens only when the track geom
      // is too big for the swisstopo profile and we have injected a fake profile
      this.factsError = hasFactsError(geojson['properties']['meta'], geojson['properties']['profile']);

      const properties = geojson.properties;
      const minmax = d3.extent(properties.profile, function(d) {
        return d[2];
      });
      properties.meta.elemin = minmax[0];
      properties.meta.elemax = minmax[1];

      // set default timetype to wander
      if (!properties.timetype) {
        properties.timetype = 'wander';
      }

      if (properties.timetype === 'mountain_biking' ||
        properties.timetype === 'skating' ||
        properties.timetype === 'canoeing' ||
        properties.timetype === 'cross_country_skiing' ||
        properties.timetype === 'sledging') {
        properties.timetype = 'no';
      }

      if (!properties.via_points.length) {
        properties.via_points = calculateViaPoints(geojson.geometry.coordinates, geojson.properties.meta.length);
      }

      return geojson;
    });
  }

  /**
   * @param {number} id the recorded track id.
   * @return {!angular.$q.Promise}
   * @private
   */
  getRecordedTrackShow_(id) {
    return this.http_.get(`${this.apiUrl_}5/recordedtracks/${id}`).then((response) => {
      const track = response.data;
      const geom = track.recorded_geom;
      const distances = track.distances && track.distances.length ? track.distances : track.viapoints_distances;

      if (!geom || !distances || geom.length !== distances.length) {
        throw new Error('Invalid coordinates');
      }
      const coordinates = geom.map(segmentCoord => segmentCoord.map(coord => lonLatToMapProjection(coord)));

      track.properties.profile = coordinates.map((segmentCoord, key) =>
        segmentCoord.map((coord, key2) => [...coord, distances[key][key2]]));

      track.recorded = true;
      track.properties.owner = track.owner;
      track.properties.filterid = track.filter_id;
      track.properties.meta = {
        length: track.properties.length || 0,
        elemin: track.properties.elemin || 0,
        elemax: track.properties.elemax || 0,
        totalup: track.properties.totalup || 0,
        totaldown: track.properties.totaldown || 0
      };
      track.geometry = {
        coordinates: coordinates,
        type: 'MultiLineString'
      };

      return track;
    });
  }

  /**
   * @param {number} id the user track id.
   * @param {boolean} recorded is track recorded
   * @return {angular.$http.HttpPromise}
   * @private
   */
  getUserTrackPOIs_(id, recorded) {
    const api = recorded ? '5/recordedtracks/' : '4/tracks/';
    return this.http_.get(`${this.apiUrl_}${api}${id}/pois`);
  }

  /**
   * @export
   * @param {boolean=} opt_print If true the printing console is also opened.
   */
  showDetailedProfile(opt_print) {
    this.appTrackProfileService_.showDetailedProfile(opt_print);
  }

  /**
   * @export
   */
  printDetailedProfile() {
    this.appTrackProfileService_.printDetailedProfile();
  }


  /**
   * @export
   */
  exportDetailedProfile() {
    this.appTrackProfileService_.exportDetailedProfile();
  }

  /**
   * @private
   */
  resetFacts_() {
    const resetFacts = () => {
      const properties = this.appTrackService.selectedTrack.properties;
      const meta = properties.meta;
      meta.length = 0;
      meta.totalup = 0;
      meta.totaldown = 0;
      meta.walking = 0;
      meta.biking = 0;
      meta.winter_hiking = 0;
      meta.snowshoe_trekking = 0;
      meta.elemin = 0;
      meta.elemax = 0;
      meta.heightdiff = 0;
      properties.profile = [];
    };
    if (this.trackInfoPromise_) {
      this.trackInfoPromise_.then(() => resetFacts());
    } else {
      resetFacts();
    }
  }

  /**
   * Round and resample the line geometry if the number of coordinates is above a specified limit.
   * @private
   * @param {ol.Feature} feature The feature to resample
   * @param {number} [limit] the maximum number of coordinates
   * @return {ol.Feature}
   */
  getEventuallyResampledLineString_(feature, limit) {
    // We round the geometry to avoid wasting bandwidth (no need for a nanometer precision!)
    // see also fetch_swisstopo_profile and parse_viapoints_as_indices in the backend

    feature = feature.clone();
    const line = /** @type {ol.geom.LineString} */ (feature.getGeometry());
    const coordinates = line.getCoordinates();
    coordinates.forEach(item => roundCoordinates(item, 10));
    line.setCoordinates(coordinates); // need to reset coordinates to update internal state

    if (!limit || coordinates.length <= limit) {
      return feature;
    } else {
      const coordinates = new Array(limit);
      for (let i = 0; i < limit; i++) {
        // While we create coordinates at regular distances the fraction goes from 0 to 1.
        // Last i is limit - 1, which is also the fraction denominator.
        const fraction = i / (limit - 1);
        coordinates[i] = roundCoordinates(line.getCoordinateAt(fraction), 10);
      }
      line.setCoordinates(coordinates);
      return feature;
    }
  }

  /**
   * @param {ol.geom.LineString} line
   * @param {Array.<Array.<number>>} heightProfile
   * @export
   */
  refreshFacts(line, heightProfile) {

    if (line.getLength() === 0) {
      // don't send empty linestring: it does crash the server
      this.resetFacts_();
      return;
    }

    const properties = this.appTrackService.selectedTrack.properties;

    const params = {
      'featureId': this.selectedTrackId || -1,
      velospeed: properties.velospeed
    };

    if (heightProfile) {
      heightProfile = heightProfile.map(p => roundCoordinates(p, 10));
      properties.profile = heightProfile;
      params['profile'] = JSON.stringify(heightProfile);
    } else {
      if (line.getCoordinates().length > MAX_POINTS_FOR_A_TRACK) {
        this.resetFacts_();
        this.factsError = true;
        return;
      }
      const feature = new olFeature(line);
      const simplifiedFeature = this.getEventuallyResampledLineString_(feature);
      params['geom'] = this.geojsonFormat_.writeFeature(simplifiedFeature);
    }
    this.trackInfoPromise_ = this.requestTrackInfo_(params, properties);
  }

  /**
   * @param {Object} params Some params.
   * @param {Object} properties The track properties.
   * @return {angular.$q.Promise}
   */
  requestTrackInfo_(params, properties) {
    const updateTrackInfo = () => {
      if (this.trackInfoCancelPromise_) {
        // cancel the previous request
        this.trackInfoCancelPromise_.resolve();
      }
      this.trackInfoCancelPromise_ = this.q_.defer();

      const data = new URLSearchParams(params).toString();
      this.trackInfoRequest = this.http_.post(this.apiUrl_ + '4/track_info', data, {
        timeout: this.trackInfoCancelPromise_.promise,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      this.increaseFactsLoading();
      return this.trackInfoRequest.then(response => {
        const meta = properties.meta;
        const data = response.data;
        meta.length = data['length'] || 0;
        meta.totalup = data['totalup'] || 0;
        meta.totaldown = data['totaldown'] || 0;
        meta.walking = data['walking'] || 0;
        meta.biking = data['biking'] || 0;
        meta.winter_hiking = data['winter_hiking'] || 0;
        meta.snowshoe_trekking = data['snowshoe_trekking'] || 0;
        if (!params['profile']) {
          const dtmKey = data['elevationmodels'];
          properties.profile = data['profile'].map(function(r) {
            return [r['easting'], r['northing'], r['alts'][dtmKey], r['dist']];
          });
        }
        const minmax = d3.extent(properties.profile, function(d) {
          return d[2];
        });
        meta.elemin = minmax[0];
        meta.elemax = minmax[1];
        this.factsError = hasFactsError(meta, properties.profile);
        this.validProfile_ = true;
        this.validateTrackInfo_();
      }).catch((response) => {
        if (response.status !== -1) {
          this.validProfile_ = false;
          this.validateTrackInfo_();
        }
      }).finally(() => {
        this.trackInfoCancelPromise_ = null;
        this.trackInfoRequest = null;
        this.decreaseFactsLoading();
      });
    };
    if (this.trackInfoRequest) {
      return this.trackInfoRequest.then(() => updateTrackInfo());
    } else {
      return updateTrackInfo();
    }
  }

  /**
   * @export
   */
  cancelDrawing() {
    this.forceTrackNameChecks_ = false;
    this.validProfile_ = true;

    if (this.modifyFeature) {
      const properties = this.appTrackService.selectedTrack.properties;
      properties.meta = /** @type {appx.TrackMeta} */ (this.trackBackup_.meta_backup);
      properties.name = /** @type {string} */ (this.trackBackup_.name_backup);
      properties.profile = /** @type {appx.TrackProfile} */ (this.trackBackup_.profile_backup);
      properties.color_rgb = this.trackBackup_.color_rgb_backup;
      properties.color = this.trackBackup_.color_backup;
      properties.opacity = this.trackBackup_.opacity_backup;
      properties.velospeed = this.trackBackup_.velospeed_backup;
      this.factsError = hasFactsError(properties.meta, properties.profile);
      this.trackBackup_ = null;
      this.modifyFeature = null;
      this.selectedTrackPoisLayer.getSource().changed();
      this.gotoTrackFacts();
    } else {
      this.clearSelectedTrack();
      this.routingState.go('track-list');
    }

    this.selectedTrackLayer.setVisible(true);

    // clear previous errors
    this.errorMessages.length = 0;

    // reset selected filter
    this.appTrackFilterService.cleanUp();
    this.appTrackFilterService.selectFilterFromTrack();
  }

  /**
   * @private
   */
  setElevationToPois_() {
    if (this.appTrackService.selectedTrackPois && this.appTrackService.selectedTrackPois['features']) {
      const pois = this.appTrackService.selectedTrackPois['features'];

      const distanceExtractor = this.appTrackProfileService_.distanceExtractor;
      const zExtractor = this.appTrackProfileService_.zExtractor;
      const bisectDistance = d3.bisector(function(d) {
        return distanceExtractor(d);
      }).left;

      const pe = this.appTrackProfileService_.poiExtractor;
      const profileData = this.appTrackService.selectedTrack['properties']['profile'];
      pois.forEach(function(d) {
        const i = bisectDistance(profileData, Math.round(pe.dist(d) * 10) / 10, 1);
        const point = profileData[i];
        if (point) {
          const z = zExtractor(point);
          pe.z(d, z);
        }
      });
    }
  }

  /**
   * @private
   * @property {boolean=} forceCheckName
   * @param {Array.<ol.Feature>=} controlPoints
   * @return {boolean} True if track info are valid, otherwise False
   */
  validateTrackInfo_(forceCheckName = false, controlPoints = undefined) {
    const track = this.appTrackService.selectedTrack;
    const trackname = track['properties']['name'];

    this.errorMessages.length = 0;

    const checkName = this.forceTrackNameChecks_ || forceCheckName;
    if (checkName && !trackname) {
      this.errorMessages.push(this.gettextCatalog.getString('track name is missing'));
      this.forceTrackNameChecks_ = true;
    }
    if (controlPoints !== undefined && controlPoints.length < 2) {
      this.errorMessages.push(this.gettextCatalog.getString('track geometry is missing'));
    }
    if (!this.validProfile_) {
      this.errorMessages.push(this.gettextCatalog.getString('The tour can not be saved'));
    }
    return this.errorMessages.length === 0;
  }

  /**
   * @export
   */
  canSaveDrawing() {
    return !this.savePromise_;
  }

  /**
   * @param {ol.geom.LineString} line
   * @param {Array.<ol.Feature>} controlPoints
   * @export
   */
  saveDrawing(line, controlPoints) {
    if (this.canSaveDrawing()) {
      this.savePromise_ = this.q_.resolve();
      const safePromise = this.trackInfoPromise_ || this.savePromise_;
      if (this.appTrackFilterService.isDirty) {
        this.appTrackFilterService.setFilterToTrack();
        safePromise.then(() => this.appTrackFilterService.save()).then(() => this.saveDrawing_(line, controlPoints));
      } else {
        safePromise.then(() => this.appTrackFilterService.setFilterToTrack()).then(() => this.saveDrawing_(line, controlPoints));
      }
    }
  }

  /**
   * @param {ol.geom.LineString} line
   * @param {Array.<ol.Feature>} controlPoints
   * @private
   */
  saveDrawing_(line, controlPoints) {
    if (!this.validateTrackInfo_(true, controlPoints)) {
      this.savePromise_ = null;
      return;
    }
    const track = this.appTrackService.selectedTrack;
    const trackProperties = track['properties'];
    const trackId = track['id'];
    const feature = new olFeature(line);
    const via_points = controlPoints.map(function(p) {
      return /** @type {ol.geom.Point} */ (p.getGeometry()).getCoordinates();
    });
    // to avoid issues with new tracks
    if (!trackProperties['profile'] || !trackProperties['profile'].length) {
      trackProperties['profile'] = line.getCoordinates().map(coord => [coord[0], coord[1], 0, 0]);
    }
    console.assert(via_points.length > 0);
    // update filterid in case it was changed
    trackProperties['filterid'] = this.appTrackFilterService.selectedFilter.id;
    const properties = {
      'color': trackProperties['color'],
      'name': trackProperties['name'],
      'via_points': via_points,
      'meta': trackProperties['meta'],
      'profile': trackProperties['profile'],
      'velospeed': trackProperties['velospeed'],
      'gpspoints': trackProperties['gpspoints'],
      'opacity': trackProperties['opacity'],
      'timetype': trackProperties['timetype'],
      'filterid': trackProperties['filterid'],
      'public': trackProperties['public']
    };
    if (trackProperties['userdate']) {
      // switch from 'dd.mm.yyyy' to 'yyyy-mm-dd'
      properties['userdate'] = trackProperties['userdate'].split('.').reverse().join('-');
    } else {
      // Allow deletion of track date
      properties['userdate'] = null;
    }
    feature.setProperties(properties);

    this.appWaiting_.increment();
    if (trackId === undefined) {
      const data = this.geojsonFormat_.writeFeatures([feature]);
      this.savePromise_ = this.http_.post(this.apiUrl_ + '4/tracks', data).then((response) => {
        const id = response.data[0];
        const name = response.data[1];
        this.cancelDrawing();
        this.addToTracksList({
          id: id,
          name: name,
          original_id: null,
          filter_id: trackProperties['filterid']
        });
        this.selectTrack(id, 'normal', {skip_zoom: true});
      }).catch((error) => {
        console.error(error);
        bootbox.alert({
          title: this.gettextCatalog.getString('An error occurred'),
          message: this.gettextCatalog.getString('An error occurred when trying to save this track')
        });
      }).finally(() => {
        this.savePromise_ = null;
        this.appWaiting_.decrement();
      });
    } else {
      const updatePromise = this.appTrackService.saveTrackConfig(track).then(() => {
        return this.getTrackMeta().then(() => {
          feature.set('meta', track.properties.meta);
          const data = this.geojsonFormat_.writeFeatures([feature]);
          return this.http_.put(this.apiUrl_ + '4/tracks/' + trackId, data)
            .then(() => {
              this.cancelDrawing();
              // update track name in this.tracks_list
              const updatedTrack = this.tracks_list.find(t => t.id === track.id);
              updatedTrack.name = feature.get('name');
              updatedTrack.filter_id = feature.get('filterid');
              this.sortTrackListByName();
              this.selectedTrackId = undefined;
              this.selectTrack(trackId, 'normal', {skip_zoom: true});
            });
        });
      });

      this.savePromise_ = updatePromise.catch((error) => {
        console.error(error);
        bootbox.alert({
          title: this.gettextCatalog.getString('An error occurred'),
          message: this.gettextCatalog.getString('An error occurred when trying to update this track')
        });
      }).finally(() => {
        this.savePromise_ = null;
        this.appWaiting_.decrement();
      });
    }
  }

  /**
   * @param {FormData} formData
   * @return {angular.$q.Promise<number>}
   * @private
   */
  importFormData_(formData) {
    this.appWaiting_.increment();
    const request = this.http_.post(this.apiUrl_ + '4/import_track', formData, {
      headers: {
        'Content-Type': undefined
      }
    });
    return request.then((response) => {
      const content = response.data;
      const success = content['success'];
      if (success) {
        const trackInfo = content['trackinfo'];
        const parsed = this.scope_.$eval(trackInfo);
        const trackId = parsed[0];
        const trackName = parsed[1];
        this.addToTracksList({id: trackId, name: trackName});
        this.selectTrack(trackId, 'normal');
        return trackId;
      } else {
        // our API uses a "msg" property for expected failure conditions
        bootbox.alert({
          message: this.gettextCatalog.getString(content['msg'])
        });
      }
    }).catch(response => {
      bootbox.alert({
        message: this.gettextCatalog.getString('Tour not imported')
      });
    }).finally(() => {
      this.appWaiting_.decrement();
    });
  }

  /**
   * @export
   */
  importGPS() {
    let trackname = $('#gps_import_form input[name="trackname"]')[0].value;
    const files = $('#gps_import_form input[name="trackfile"]')[0].files;
    if (!files || files.length < 1) {
      // Invalid form. Workaround to issue with Angular form validation.
      // Do not close the modal.
      return;
    }
    if (!trackname) {
      const filename = files[0].name;
      // filename without path and extension
      trackname = filename.replace(/^.*[\\\/]/, '').replace(/\..*$/, '');
      trackname = trackname.substring(0, 40); // max namelength is 40
    }

    const formData = new FormData();
    formData.append('trackname', trackname);
    formData.append('trackfile', files[0]);

    this.importFormData_(formData);

    // Finally close the modal and clean it.
    $('#gps_import_modal').modal('hide');
  }

  /**
   * @export
   * @param {ol.Feature} activePOI
   */
  poiChanged(activePOI) {
    this.activePOI_ = activePOI;
  }

  /**
   * @private
   */
  loginChange_() {
    const trackFromCoordinates = this.trackFromCoordinates_;
    const import_title = this.gettextCatalog.getString('Track import box title');
    const import_text = this.gettextCatalog.getString('Track import box message');

    if (this.track_id_param) {
      const selectedTrack = this.appTrackService.selectedTrack;
      if (!selectedTrack || selectedTrack.id !== this.track_id_param) {
        const trackParam = this.ngeoLocation_.hasParam('trackId') ? 'trackId' : 'recordedTrackId';
        const type = trackParam === 'recordedTrackId' ? 'recordedtrack' : 'normal';
        this.selectTrack(this.track_id_param, type).catch(err => {
          this.onImportError_(err);
        });
      }
    } else if (trackFromCoordinates) {
      if (this.appTrackService.selectedTrack !== trackFromCoordinates) {
        this.appTrackService.selectedTrack = trackFromCoordinates;
        const line = new olGeomLineString(trackFromCoordinates.geometry.coordinates);
        const feature = new olFeature(line);
        const simplifiedFeature = this.getEventuallyResampledLineString_(feature, 5000);
        const geojson = this.geojsonFormat_.writeFeature(simplifiedFeature);
        this.requestTrackInfo_({
          'featureId': -1,
          'geom': geojson
        },
        trackFromCoordinates.properties).then(() => {
          if (trackFromCoordinates) { // Make GCC happy :/
            this.handleUserTrackShow_(trackFromCoordinates);
          }
        });
      }
    }

    if (this.authentication_.loggedIn) {
      this.getUserTracksList_(data => {
        if (this.track_id_param) {
          this.importTrack_(this.track_id_param, data);
        } else if (trackFromCoordinates) {
          this.importTrackFromCoordinates_();
        }
      });
      // (re)initialize filters
      this.appTrackFilterService.updateList();
      this.appTrackFilterService.init();
    } else {
      const showDialog = this.ngeoLocation_.getParam('showDialog');
      if (trackFromCoordinates && showDialog === 'importcoordinates') {
        this.ngeoLocation_.deleteParam('showDialog');
        bootbox.alert({
          title: import_title,
          message: import_text
        });
      }
    }
  }

  addToTracksList(item) {
    this.tracks_list.push(item);
    this.sortTrackListByName();
  }

  /**
   * @export
   * @return {boolean}
   */
  displayReturnToTrackList() {
    return (!!this.track_id_param || !!this.trackFromCoordinates_) && !this.loggedIn();
  }

  editRecordedTrackName() {
    const box = bootbox.prompt({
      message:
          `${this.gettextCatalog.getString('Copy and change recorded tour description')}
            <br>
            <button type="button" class="btn btn-primary copy-recorded-tour-btn">
                ${this.gettextCatalog.getString('Copy and change recorded tour')}
            </button>
            <br>
            <br>
           <h5 style="margin: 10px 0;">${this.gettextCatalog.getString('Edit name')}</h5>`,
      maxlength: 40,
      required: true,
      value: this.appTrackService.selectedTrack.properties.name,
      buttons: {
        confirm: {
          label: this.gettextCatalog.getString('Save')
        },
        cancel: {
          label: this.gettextCatalog.getString('Cancel')
        }
      },
      title: this.gettextCatalog.getString('Edit track'),
      placeholder: this.gettextCatalog.getString('name field placeholder text'),
      callback: trackName => {
        if (trackName) {
          const track = this.appTrackService.selectedTrack;
          const properties = this.appTrackService.createRecordedTrackPropertiesForUpdate(track.properties);
          properties.name = trackName;
          this.http_.put(`${this.apiUrl_}5/recordedtracks/${track.id}`, {properties}).then(() => {
            const updatedTrack = this.tracks_list.find(t => t.id === track.id);
            updatedTrack.name = trackName;
            track.properties.name = trackName;
            this.sortTrackListByName();
          }).catch((err) => {
            if (err.status !== 200) {
              if (err.status === 401) {
                this.authentication_.delog();
              } else {
                bootbox.alert({message: this.gettextCatalog.getString('Error when saving track config')});
              }
            }
          });
        }
      }
    });
    box.on('shown.bs.modal', () => {
      const inputElem = $("input.bootbox-input-text");
      const acceptBtnElem = $("button.bootbox-accept");
      inputElem.focus();
      inputElem.on('input', (evt) => {
        if (!evt.target.value.length) {
          acceptBtnElem.attr('disabled', 'disabled');
        } else if (acceptBtnElem.attr('disabled')) {
          acceptBtnElem.removeAttr('disabled');
        }
      });
      box.find('input').attr({'maxlength': 40}).prop({'required': true});
      $('button.copy-recorded-tour-btn').on('click', () => {
        bootbox.hideAll();
        this.duplicateTrack();
      });
    });
  }

  sortTrackListByName() {
    this.tracks_list.sort((left, right) => {
      const titleLeft = left.name.toLowerCase();
      const titleRight = right.name.toLowerCase();
      return titleLeft > titleRight ? 1 : titleLeft < titleRight ? -1 : 0;
    });
  }

  duplicateTrack() {
    let copyName = `${this.gettextCatalog.getString('copy of')} ${this.appTrackService.selectedTrack.properties.name}`;
    if (copyName.length > 40) {
      copyName = `${copyName.substr(0, 37)}...`;
    }
    const track = this.appTrackService.selectedTrack;
    const properties = {'name': copyName, 'recordedtrack': false};
    const filterid = track.properties.filterid;
    const isRecorded = this.appTrackService.selectedTrack.recorded;

    if (isRecorded) {
      properties.recordedtrack = true;
      properties.profile = track.properties.profile;
    }
    this.appWaiting_.increment();
    this.http_.post(`${this.apiUrl_}5/tracks/${track.id}/duplicate`, {properties}).then((response) => {
      const trackInfo = response['data'];
      const trackId = trackInfo['id'];
      const trackName = trackInfo['name'];
      this.addToTracksList({id: trackId, name: trackName, filter_id: filterid});
      this.selectTrack(trackId, 'normal').then(() => {
        bootbox.alert({
          title: isRecorded ?
            this.gettextCatalog.getString('Copy recorded tour in tourlist') :
            this.gettextCatalog.getString('Copy tour in tourlist'),
          message: isRecorded ?
            this.gettextCatalog.getString('Recorded tour copied') :
            this.gettextCatalog.getString('Tour copied')
        });
        this.routingState.go('track-edit', {
          'create': false
        });
      });
      this.sortTrackListByName();
    }).catch((err) => {
      if (err.status !== 200) {
        if (err.status === 401) {
          this.authentication_.delog();
        } else {
          bootbox.alert(
            {
              message: this.gettextCatalog.getString('The tour can not be saved')
            });
        }
      }
    }).finally(() => this.appWaiting_.decrement());
  }

  showTrackPoints(trackSource, lineFeature, properties, coordinates) {
    const showPausePoints = Array.isArray(coordinates[0][0]);
    let pointA = coordinates[0];
    let pointB = coordinates[coordinates.length - 1];
    const pausePoints = [];
    if (showPausePoints) {
      pointA = coordinates[0][0];
      const lastSegment = coordinates[coordinates.length - 1];
      pointB = lastSegment[lastSegment.length - 1];
      coordinates.forEach((segmentPoints, idx) => {
        if (idx !== 0) {
          pausePoints.push(segmentPoints[0]);
        }
        if (idx !== coordinates.length - 1) {
          pausePoints.push(segmentPoints[segmentPoints.length - 1]);
        }
      });
    }
    const featureA = new olFeature(new olGeomPoint(pointA));
    const featureB = new olFeature(new olGeomPoint(pointB));

    let lineStringGeometry = lineFeature.getGeometry();
    if (lineStringGeometry.getType() === 'MultiLineString') {
      lineStringGeometry = new olGeomLineString(lineStringGeometry.getCoordinates().flat());
    }

    const pointStyle = (resolution, print, text) => {
      const lineOpacity = lineFeature.get('opacity') || 100;
      const opacity = lineOpacity / 100;
      const color = lineFeature.get('color_rgb');
      const styleCacheKey = `${print}-${text}-${opacity}-${color}`;
      if (this.trackPointsStyleCache_[styleCacheKey]) {
        return this.trackPointsStyleCache_[styleCacheKey];
      }
      const radius = print ? 6 : 8;

      const startEndDistance = olDistance(pointA, pointB);
      const distanceInPixels = startEndDistance / resolution;
      if (distanceInPixels <= radius && opacity > 0) {
        const shiftedPosition = shiftPositionAlongLine(lineStringGeometry, radius, resolution, print);
        featureB.getGeometry().setCoordinates(shiftedPosition);
      }
      const styles = [
        new olStyleStyle({
          image: new olStyleCircle({
            radius: radius,
            fill: new olStyleFill({color: colorWithOpacity(color, opacity)}),
            stroke: new olStyleStroke({
              color: [0, 0, 0, 0] // for correct work in print (if stroke not passed, it black by default)
            })
          }),
          text: new olStyleText({
            text: text,
            fill: new olStyleFill({color: color === '#ffe500' ? [0, 0, 0, opacity] : [255, 255, 255, opacity]}),
          })
        })
      ];
      this.trackPointsStyleCache_[styleCacheKey] = styles;

      return styles;
    };
    featureA.setStyle((feature, resolution, print) => pointStyle(resolution, print, 'A'));
    featureB.setStyle((feature, resolution, print) => pointStyle(resolution, print, 'B'));
    featureA.setProperties({...properties, type: 'track'});
    featureB.setProperties({...properties, type: 'track'});
    trackSource.addFeature(featureA);
    trackSource.addFeature(featureB);
    pausePoints.forEach(point => {
      const featureP = new olFeature(new olGeomPoint(point));
      featureP.setStyle((feature, resolution, print) => pointStyle(resolution, print, 'II'));
      featureP.setProperties({...properties, type: 'track'});
      trackSource.addFeature(featureP);
    });
  }

  increaseFactsLoading() {
    ++this.factsLoading;
  }

  decreaseFactsLoading() {
    // this is to avoid blinking
    this.timeout_(() => --this.factsLoading, 500);
  }
}

/**
 * @const {!angular.Component}
 */
const component = {
  bindings: {
    'allowTrackImport': '<',
    'onSecondLineChanged': '&',
    'onShowTracksMenu': '&'
  },
  controller: TracksController,
  templateUrl: 'js/app/track/tracks.html'
};

export default angular.module('appTracksComponent', [
  ngeoStatemanagerLocationModule.name,
  appTrackProfile.name,
  appTrackService.name,
  appTrackFilterService.name,
  appTrackProfileService.name,
  appTrackPrintService.name])
  .component('appTracks', component);
